"use client";
import { usePoints, useStaticStore, useToast, useUserPoints } from "app/hooks";
import { ProductModel, RentalRequestModel } from "@api/models";
import { STORE_KEYS } from "app/constants";
import { CoinIcon, FreeTicketIcon } from "./Icons";
import { logError } from "@lib/logger";
import { useEffect } from "react";
import { pollingChargeStatus } from "@lib/utils";
import { usePathname } from "next/navigation";
import { ResponseError } from "@api/runtime";
import {
  deletePaymentIntentInfoFromCookieOnClient,
  getPaymentIntentInfoFromCookieOnClient,
} from "@lib/paymentUtils";
import { GLOBALS } from "config";

export default function ChargeProcessing() {
  const { results: points, isLoading: isPointLoading } = usePoints();
  const {
    data: isOpen,
    isLoading,
    mutate: mutateIsDialogOpen,
  } = useStaticStore(STORE_KEYS.isChargeProcessingDialogOpen);
  const { mutate: userPointsMutate } = useUserPoints();
  const { pushToast } = useToast();
  const pathname = usePathname();
  const paymentIntentInfoCookie = getPaymentIntentInfoFromCookieOnClient();

  let product: ProductModel = {
    id: "",
    type: "coin",
    name: "",
    price: 0,
    currency: "",
    paymentMethods: [],
  };

  const rentalArticles = (
    articleIds: string[],
    seriesId: string,
    totalPrice: number,
    ticketCount: number,
  ) => {
    fetch("/api/checkout/rental", {
      method: "POST",
      body: JSON.stringify({
        articleIds: articleIds,
        ...(articleIds.length !== 1 && { seriesId: seriesId }),
        amount: totalPrice,
        ticketCount: ticketCount,
      } as RentalRequestModel),
    })
      .then(async (res) => {
        if (res.status !== 200) {
          throw new ResponseError(res);
        }
        pushToast(`エピソードを${GLOBALS.IS_RENTAL ? "レンタル" : "購入"}しました。`);
        setTimeout(() => {
          mutateIsDialogOpen(false);
          window.location.reload();
        }, 1000);
      })
      .catch((e) => {
        logError("server error: checkout");
        pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
          type: "error",
        });
        setTimeout(() => {
          mutateIsDialogOpen(false);
        }, 1000);
      });
  };
  useEffect(() => {
    if (paymentIntentInfoCookie === null) return;
    if (!isOpen || isLoading || isPointLoading) return;
    const fetchStatusAndRental = async () => {
      if (!product.id) return;
      await pollingChargeStatus(paymentIntentInfoCookie.paymentIntentId)
        .then(async (res) => {
          if (res === undefined) {
            throw new Error();
          }
          if (res.status === "completed") {
            // TODO: 表示するメッセージ内容検討(ボーナス含めるか等)
            pushToast(`${product.points?.value}コインを購入しました。`);
            const { articleIds, seriesId, amount, ticketCount } = paymentIntentInfoCookie;
            // レンタルがあればレンタル
            if (articleIds === undefined || articleIds == null) {
              setTimeout(() => {
                mutateIsDialogOpen(false);
                userPointsMutate();
              }, 1000);
              return;
            }
            if (articleIds?.length !== 0) {
              if (articleIds?.length === 1 && seriesId === undefined) {
                // 単体エピソード
                // 現在のページのidと保持していたarticleIdが同一か確認、違えばレンタル処理は行わない
                if (articleIds[0] && pathname.includes(`/episodes/${articleIds[0]}`)) {
                  rentalArticles(articleIds, seriesId || "", amount || 0, ticketCount || 0);
                } else {
                  logError("server error: checkout");
                  pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
                    type: "error",
                  });
                  setTimeout(() => {
                    mutateIsDialogOpen(false);
                  }, 1000);
                  return;
                }
              } else {
                // まとめてレンタル
                // 現在のページのidと保持していたseriesIdが同一か確認、違えばレンタル処理は行わない
                if (seriesId && pathname.includes(`/series/${seriesId}/bulk`)) {
                  rentalArticles(articleIds, seriesId, amount || 0, ticketCount || 0);
                } else {
                  logError("server error: checkout");
                  pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
                    type: "error",
                  });
                  setTimeout(() => {
                    mutateIsDialogOpen(false);
                  }, 1000);
                  return;
                }
              }
            }
          } else if (res.status === "failed") {
            logError(res.errorCode + ": " + res.errorMessage);
            throw new Error();
          }
        })
        .catch((e) => {
          logError("server error: checkout");
          pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
            type: "error",
          });
          setTimeout(() => {
            mutateIsDialogOpen(false);
          }, 1000);
        })
        .finally(() => {
          deletePaymentIntentInfoFromCookieOnClient();
        });
    };
    fetchStatusAndRental();
  }, [isOpen, isPointLoading]);

  useEffect(() => {
    if (paymentIntentInfoCookie === null) {
      mutateIsDialogOpen(false);
    }
  }, [paymentIntentInfoCookie]);

  if (isLoading || isPointLoading) return <div className="g-loading">loading</div>;

  if (paymentIntentInfoCookie === null) return <></>;

  product = points?.points.filter((p) => {
    return p.id === String(paymentIntentInfoCookie.pointTypeId);
  })[0];

  if (isOpen && !isLoading) {
    return (
      <>
        <div>
          <h1 className="dialog-h">チャージ処理中</h1>
          <div className="dialog-pay-h">
            <div className="dialog-pay-h-main">
              <span className="dialog-pay-h-title">
                <CoinIcon size={18} className="dialog-pay-icon" />
                {product.points?.value.toLocaleString()}
                {product.points?.bonusValue !== 0 && (
                  <>
                    <span>+</span>
                    <FreeTicketIcon size={18} className="dialog-pay-icon" />
                    {product.points?.bonusValue.toLocaleString()}枚
                  </>
                )}
              </span>
              <div className="dialog-pay-h-price mode-charge">
                ¥{product.price.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="g-loading">loading</div>
        </div>
      </>
    );
  }
}
